import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import store from "../../../../../reducks/store/AppTrigger/store";
import ReactModal from "react-modal";
import styles from "./AddOutputModal.module.scss";
import { OutputValue } from "../types/types";
import { getImageUrl } from "../../../../../commons/assets_path";
import { modalStyle } from "./AddOutputModalStyle";
import { AddOutputList } from "./AddOutputList";
import { EditOutput } from "./EditOutput";
import axios from "axios";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
import { useTranslation } from "react-i18next";

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  outputLabels: OutputValue[];
  setOutputLabels: (outputLabels: OutputValue[]) => void;
  serviceIconName: string;
  selectableDataType: string;
  templateClassName: string;
  output_limit_size: number;
};

export const AddOutputModal = ({
  isOpen,
  onRequestClose,
  outputLabels,
  setOutputLabels,
  serviceIconName,
  selectableDataType,
  templateClassName,
  output_limit_size,
}: Props) => {
  const { t } = useTranslation();

  const outputList = useSelector((state: any) => state);
  const [editOutput, setEditOutput] = useState<{ isEdit: boolean; index: number; output: OutputValue | null }>({
    isEdit: false,
    index: -1,
    output: {
      target_id: 0,
      display_name: "",
      readonly_test_value: "",
      test_value: "",
      data_type: "",
      is_duplicated: false,
    },
  });

  const transformResultData = (data: any) => {
    return data.result.map((element: any) => {
      if (element === null) {
        return "";
      } else if (typeof element === "object") {
        return JSON.stringify(element);
      } else {
        return element;
      }
    });
  };

  // 検索結果から追加する値に追加する
  const searchResultAddOutput = async () => {
    if (exceededOutputLimit) return;
    const apiRequestTestResult = $(".js-api_request_test_result").val();
    const jsonPathInputValue = $("#json_path_search_form").val() as string;
    if (!jsonPathInputValue) return;
    const res = await axios.post("ajax_json_path_test", {
      api_request_test_result: apiRequestTestResult,
      json_path: jsonPathInputValue,
    });

    // NOTE: data[1].idやdata[2].nameのような場合、idやnameの部分をdisplay_nameに設定する
    const jsonPathInputValueArray = jsonPathInputValue?.split(".");
    const displayName = jsonPathInputValueArray[jsonPathInputValueArray.length - 1];

    if (res?.data?.result) {
      const resultData = transformResultData(res.data);
      store.dispatch({
        type: "ADD_OUTPUT",
        output: {
          target_id: null,
          display_name: displayName,
          name: jsonPathInputValue,
          test_value: resultData.join("[,]"),
          readonly_test_value: resultData.join(","),
          data_type: "text",
        },
      });
    }
  };

  const exceededOutputLimit =
    outputLabels.filter((item) => !item._destroy).length + outputList.length >= output_limit_size;

  const updatePickButtons = useMemo(
    () => (isDisabled: boolean) => {
      const imgSrc = getImageUrl(isDisabled ? "images/icons/addEnclosedGray.svg" : "images/icons/addEnclosed.svg");
      const imgStyle = isDisabled ? "none" : "auto";

      document.querySelectorAll<HTMLImageElement>(".js-jsonpath-pickBtn img").forEach((img) => {
        img.src = imgSrc;
        img.closest("span")?.style.setProperty("pointer-events", imgStyle);
      });
    },
    [],
  );

  useEffect(() => {
    updatePickButtons(exceededOutputLimit);
  }, [exceededOutputLimit, updatePickButtons]);

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      style={modalStyle as ReactModal.Styles}
    >
      <div className={styles.modalBody}>
        <div className={styles.modalDescription}>
          {t("AddOutputModal.追加したい値の")}
          <img
            src={getImageUrl("images/icons/addEnclosed.svg")}
            width={20}
            height={20}
            style={{ verticalAlign: "sub" }}
          />
          {t("AddOutputModal.をクリックしてください。 詳しくは")}
          <a href={t("AddOutputModal.「取得する値」を追加する方法")} target="_blank" rel="noreferrer">
            {t("AddOutputModal.こちら")}
          </a>
          {t("AddOutputModal.period")}
        </div>
        <img
          src={getImageUrl("images/icons/cancel.svg")}
          className={styles.closeImg}
          onClick={() => {
            onRequestClose();
            store.dispatch({ type: "RESET" });
          }}
        />
        <div className={styles.modalContent}>
          <div id="jsonPathTestResultAreaPreview" className={styles.jsonPathTestResultAreaPreview}>
            <div className={styles.searchField}>
              <input
                id="json_path_search_form"
                className={styles.searchForm}
                type="text"
                name="json_path_input_form"
                placeholder={t("AddOutputModal.jsonPathExample")}
              />
              <div
                className={`${styles.addJsonPath} ${exceededOutputLimit ? styles.disabled : ""}`}
                onClick={() => searchResultAddOutput()}
                data-testid="addJsonPath"
              >
                <img
                  src={getImageUrl(exceededOutputLimit ? "images/icons/addGray.svg" : "images/icons/addPrimary.svg")}
                  width={20}
                  height={20}
                />
                {t("AddOutputModal.JSONPath指定")}
              </div>
            </div>
            <pre className={`${styles.jsonPathTestResultArea} js-json_path_test_result_area`}></pre>
          </div>
          <AddOutputList outputList={outputList} serviceIconName={serviceIconName} setEditOutput={setEditOutput} />
        </div>
        <input type="hidden" className="js-api_request_test_result" />
        <button
          id="addOutput"
          className={styles.addButton}
          onClick={() => {
            setOutputLabels([...outputLabels, ...outputList]);
            onRequestClose();
            store.dispatch({ type: "RESET" });
          }}
        >
          {t("AddOutputModal.追加")}
        </button>
        {editOutput.isEdit && (
          <EditOutput
            editOutput={editOutput}
            setEditOutput={setEditOutput}
            selectableDataType={selectableDataType}
            templateClassName={templateClassName}
            outputList={outputList}
            outputLabels={outputLabels}
            transformResultData={transformResultData}
          />
        )}
      </div>
    </ReactModal>
  );
};

export default AddOutputModal;
